import { Head } from "@inertiajs/react";
import { BookOpen, TrendingUp, Medal } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import DonationCard from "@/components/public/DonationCard";
import StickyDonateBar from "@/components/public/StickyDonateBar";
import SupportersList from "@/components/public/SupportersList";
import RankingCard from "@/components/public/RankingCard";
import SchoolProgress from "@/components/public/SchoolProgress";
import StudentShareDialog from "@/components/shared/StudentShareDialog";
import MilestonesCard from "@/components/schools/MilestonesCard";
import ProgressBar from "@/components/shared/ProgressBar";
import StatTile from "@/components/shared/StatTile";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, percentOf } from "@/lib/format";

/**
 * Donor-facing student page, at the non-enumerable public_slug URL a
 * family shares. Privacy-safe only: first name + last initial, no
 * photos, the classroom's name but not its roster. The school's goal
 * and rewards show what a donation adds up to.
 */
export default function Show({
  student, classroom, ranking, campaign, school,
  milestones, classroom_milestones = [],
  donations, donate_path, public_url,
  donation_state, donations_open_on, fee_schedule, square, pretend_payments,
}) {
  const open = donation_state === "open";

  return (
    <PublicLayout doodles mainClassName={`max-w-5xl py-10 md:py-14 ${open ? "pb-28 lg:pb-14" : ""}`}>
      <Head title={`Support ${student.display_name} · ${school.name}`} />

      <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-start">
        {/* ── The story: who, for what, how they're doing ── */}
        <div className="min-w-0 space-y-6">
          <div className="space-y-2">
            <h1 className="font-display text-3xl font-semibold leading-tight text-foreground md:text-4xl">
              {student.display_name} is reading for {school.name}
            </h1>
            <p className="leading-relaxed text-muted-foreground">
              {formatRoom(classroom.name)}{classroom.grade && ` · ${formatGrade(classroom.grade)}`} · {campaign.name}
            </p>
            {campaign.description && <p className="leading-relaxed text-foreground">{campaign.description}</p>}
          </div>

          <section className="space-y-3" aria-label={`${student.first_name}'s progress`}>
            <div className="grid grid-cols-2 gap-3">
              <StatTile icon={TrendingUp} label="Raised" value={formatMoney(student.raised_cents)} color="primary" />
              <StatTile icon={BookOpen} label="Minutes read" value={formatNumber(student.minutes_logged)} color="forest" />
            </div>
            <PersonalGoals student={student} />
            {ranking.total_students > 1 && (
              <RankingCard ranking={ranking} firstName={student.first_name} classroomName={formatRoom(classroom.name)} />
            )}
          </section>

          {donations.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent supporters</h2>
              <SupportersList donations={donations} />
            </section>
          )}
        </div>

        {/* ── The ask (sticky beside the story on desktop) ── */}
        <div className="space-y-4 lg:sticky lg:top-6">
          <DonationCard
            school={school}
            donatePath={donate_path}
            donationState={donation_state}
            donationsOpenOn={donations_open_on}
            feeSchedule={fee_schedule}
            square={square}
            pretendPayments={pretend_payments}
            title={`Support ${student.first_name}`}
            description={`Every donation goes to ${school.name}.`}
            messagePlaceholder="Keep reading!"
          />
          {public_url && (
            <div className="flex justify-center">
              <StudentShareDialog
                url={public_url}
                firstName={student.first_name}
                schoolName={school.name}
                triggerLabel="Share this page"
                triggerVariant="outline"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── The bigger picture: classroom and school ── */}
      <div className="mt-14 space-y-10">
        <ClassroomProgress classroom={classroom} />
        {classroom_milestones.length > 0 && (
          <MilestonesCard
            milestones={classroom_milestones}
            manage_path={null}
            title={`${formatRoom(classroom.name)} rewards`}
            unlockedSummary={({ unlocked, total }) => `${unlocked} of ${total} unlocked — rewards ${formatRoom(classroom.name)} earns together.`}
          />
        )}
        <SchoolProgress school={school} campaign={campaign} raisedCents={campaign.total_raised_cents} minutesLogged={campaign.total_minutes_logged} count={campaign.donation_count} />
        {milestones.length > 0 && <MilestonesCard milestones={milestones} manage_path={null} title="School rewards" />}
      </div>

      {open && <StickyDonateBar label={`Donate to ${student.first_name}`} />}
    </PublicLayout>
  );
}

// Goals the family set, if any.
function PersonalGoals({ student }) {
  const minuteGoal = student.personal_minute_goal;
  const moneyGoal = student.personal_money_goal_cents;
  if (!minuteGoal && !moneyGoal) return null;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card px-5 py-4">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Medal className="h-4 w-4" aria-hidden="true" />
        {student.first_name}'s goal{minuteGoal && moneyGoal ? "s" : ""}
      </p>
      {moneyGoal && (
        <ProgressBar label="Raised" detail={`${formatMoney(student.raised_cents)} / ${formatMoney(moneyGoal)}`} pct={percentOf(student.raised_cents, moneyGoal)} />
      )}
      {minuteGoal && (
        <ProgressBar label="Minutes read" detail={`${formatNumber(student.minutes_logged)} / ${formatNumber(minuteGoal)}`} pct={percentOf(student.minutes_logged, minuteGoal)} barColor="bg-accent" />
      )}
    </div>
  );
}

// Only when the teacher has set classroom goals.
function ClassroomProgress({ classroom }) {
  if (!classroom.money_goal_cents && !classroom.minute_goal) return null;

  return (
    <section className="space-y-4">
      <h2 className="font-display text-xl font-semibold text-foreground">How {formatRoom(classroom.name)} is doing</h2>
      <div className="space-y-3">
        {classroom.money_goal_cents && (
          <ProgressBar
            label="Fundraising goal"
            detail={`${formatMoney(classroom.raised_cents)} / ${formatMoney(classroom.money_goal_cents)}`}
            pct={percentOf(classroom.raised_cents, classroom.money_goal_cents)}
          />
        )}
        {classroom.minute_goal && (
          <ProgressBar
            label="Reading goal"
            detail={`${formatNumber(classroom.minutes_logged)} / ${formatNumber(classroom.minute_goal)} min`}
            pct={percentOf(classroom.minutes_logged, classroom.minute_goal)}
            barColor="bg-accent"
          />
        )}
      </div>
    </section>
  );
}

// Errors show inline next to the form, not as a toast too.
Show.inlineFlashAlerts = true;
